import React, { useState } from 'react';
import axios from 'axios';
import {
    Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogContent,
    DialogTitle, Divider, FormControl, Grid, IconButton, InputAdornment, InputLabel,
    MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography, Tooltip, withStyles, DialogActions, FormGroup, FormControlLabel, Checkbox
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import moment from 'moment-timezone';
import { makeStyles } from '@material-ui/core/styles';
import ErrorIcon from '@material-ui/icons/Error';
import EditIcon from '@material-ui/icons/Edit';
import InfoIcon from '@material-ui/icons/Info';
import CloseIcon from '@material-ui/icons/Close';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import VisibilityIcon from '@material-ui/icons/Visibility';
import TrophyIcon from '@material-ui/icons/EmojiEvents';
import SortIcon from '@material-ui/icons/Sort';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import TimerIcon from '@material-ui/icons/Timer';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import GroupIcon from '@material-ui/icons/Group';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
// 在文件顶部导入部分添加以下导入
import Container from '@material-ui/core/Container';
import SearchIcon from '@material-ui/icons/Search';
import './QuestLeaderboard.scss';
import {
    formatMinutesToHoursAndMinutes,
    formatValueWithUnit,
    getLocalTimeString,
    isQuestOver,
    isQuestPaid,
    isAutoStreamQuest
} from '../../../utils/functions';

// 添加 PromptDialog 导入
import PromptDialog from '../../dialog/PromptDialog';

// 在文件顶部的导入部分添加以下组件
import SyncIcon from '@material-ui/icons/Sync';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import Alert from '@material-ui/lab/Alert';

import DeleteIcon from '@material-ui/icons/Delete';
import WarningIcon from '@material-ui/icons/Warning';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';

const useStyles = makeStyles((theme) => ({
    container: {
        backgroundColor: '#1e1e28',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-2px)',
        },
    },
    searchForm: {
        display: 'flex',
        marginBottom: theme.spacing(3),
        alignItems: 'center',
        width: '100%',
    },
    searchField: {
        flex: '1 1 auto',
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#252530',
            '& fieldset': {
                borderColor: '#444',
                transition: 'border-color 0.3s',
            },
            '&:hover fieldset': {
                borderColor: '#666',
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
            },
        },
        '& .MuiOutlinedInput-input': {
            color: '#fff',
            padding: '16px',
        },
        '& .MuiInputLabel-outlined': {
            color: '#888',
            '&.Mui-focused': {
                color: theme.palette.primary.main,
            },
        },
    },
    searchButton: {
        height: 56,
        marginLeft: theme.spacing(1.5),
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        padding: '10px 20px',
        minWidth: 150,
        flex: '0 0 150px',
        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
        transition: 'all 0.3s',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: '0 4px 12px rgba(33, 150, 243, 0.5)',
        },
        '&.Mui-disabled': {
            backgroundColor: 'rgba(33, 150, 243, 0.3)',
        },
    },
    // 修改表格容器样式支持横向滚动
    tableContainer: {
        backgroundColor: '#252530',
        borderRadius: 8,
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
        overflow: 'auto', // 修改为 auto 而非 hidden
        position: 'relative', // 添加相对定位
        '&::-webkit-scrollbar': {
            height: 8,
            backgroundColor: '#1e1e28',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#444',
            borderRadius: 4,
        },
    },

    // 确保表格单元格内容不换行
    tableCell: {
        color: '#fff',
        borderBottomColor: '#333',
        padding: '16px 12px', // 减小水平内边距
        whiteSpace: 'nowrap', // 防止内容换行
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    tableHeaderCell: {
        color: '#9b9b9b',
        fontWeight: 500,
        letterSpacing: '1.2px',
        fontSize: '14px',
        borderBottom: '2px solid #333',
        padding: '16px 24px',
        backgroundColor: '#1e1e28',
    },
    // 固定右侧操作列
    actionColumn: {
        position: 'sticky',
        right: 0,
        backgroundColor: '#1e1e28', // 与表头背景色匹配
        boxShadow: '-5px 0 5px rgba(0, 0, 0, 0.2)', // 添加阴影效果
        zIndex: 2,
    },

    // 给表头的操作列添加相同样式
    actionColumnHeader: {
        position: 'sticky',
        right: 0,
        backgroundColor: '#1e1e28',
        boxShadow: '-5px 0 5px rgba(0, 0, 0, 0.2)',
        zIndex: 3,
    },

    // 修改表格整体样式
    table: {
        tableLayout: 'fixed', // 固定表格布局
        minWidth: 1200, // 确保表格有最小宽度
    },
    // 修改表格行样式
    tableRow: {
        transition: 'background-color 0.2s',
        '&:nth-of-type(odd)': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
        },
        '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.05)',
        },
        '&.top-rank': {
            backgroundColor: 'rgba(33, 150, 243, 0.05)',
        },
        '&.rank-1': {
            backgroundColor: 'rgba(255, 215, 0, 0.05)',
        },
        '&.rank-2': {
            backgroundColor: 'rgba(192, 192, 192, 0.05)',
        },
        '&.rank-3': {
            backgroundColor: 'rgba(205, 127, 50, 0.05)',
        },
    },
    errorMessage: {
        color: '#ff5a5f',
        margin: theme.spacing(2, 0),
        padding: theme.spacing(1.5, 2),
        backgroundColor: 'rgba(255, 90, 95, 0.1)',
        borderRadius: theme.shape.borderRadius,
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        border: '1px solid rgba(255, 90, 95, 0.2)',
        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
        },
    },
    noData: {
        textAlign: 'center',
        padding: '40px 0',
        color: '#999',
        backgroundColor: '#252530',
        borderRadius: 8,
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    },
    questDetails: {
        backgroundColor: '#252530',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(3),
        width: '100%',
        animation: '$fadeIn 0.4s ease-out',
    },
    questTitle: {
        color: '#fff',
        marginBottom: theme.spacing(2),
        fontSize: 22,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            marginRight: theme.spacing(1.5),
            color: theme.palette.primary.main,
        },
    },
    statsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    statCard: {
        backgroundColor: '#1e1e28',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        flex: '1 1 200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    statValue: {
        color: theme.palette.primary.main,
        fontSize: '24px',
        fontWeight: 'bold',
        marginTop: theme.spacing(1),
    },
    statLabel: {
        color: '#9b9b9b',
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
    },
    statusChip: {
        margin: theme.spacing(0, 1, 1, 0),
        '& .MuiChip-label': {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
        },
    },
    activeStatus: {
        backgroundColor: 'rgba(76, 175, 80, 0.2)',
        color: '#4caf50',
    },
    inactiveStatus: {
        backgroundColor: 'rgba(244, 67, 54, 0.2)',
        color: '#f44336',
    },
    paidStatus: {
        backgroundColor: 'rgba(33, 150, 243, 0.2)',
        color: '#2196f3',
    },
    questDetailItem: {
        display: 'flex',
        marginBottom: theme.spacing(1),
        borderBottom: '1px solid #333',
        paddingBottom: theme.spacing(1),
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    questDetailLabel: {
        color: '#9b9b9b',
        fontWeight: 500,
        width: 150,
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            marginRight: theme.spacing(1),
            fontSize: '0.9rem',
        },
    },
    questDetailValue: {
        color: '#fff',
        fontWeight: 400,
        flexGrow: 1,
    },
    rankCell: {
        fontWeight: 'bold',
        '& .rank-1': { color: '#FFD700' },
        '& .rank-2': { color: '#C0C0C0' },
        '& .rank-3': { color: '#CD7F32' },
    },
    tablePagination: {
        color: '#fff',
        '& .MuiIconButton-root': {
            color: '#fff',
        },
        '& .MuiSelect-icon': {
            color: '#fff',
        }
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: 0,
            transform: 'translateY(10px)'
        },
        '100%': {
            opacity: 1,
            transform: 'translateY(0)'
        }
    },
    description: {
        color: '#aaa',
        fontSize: '14px',
        marginBottom: theme.spacing(3),
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            marginRight: theme.spacing(1),
            fontSize: '1.2rem',
            color: '#888',
        },
    },
    // 添加到样式定义中
    detailButton: {
        minWidth: 'auto',
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        color: theme.palette.primary.main,
        padding: '6px',
        borderRadius: '50%', // 圆形按钮
        '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.2)',
            boxShadow: '0 2px 6px rgba(33, 150, 243, 0.3)',
        },
        transition: 'all 0.2s ease-in-out',
    },
    dialogTitle: {
        backgroundColor: '#252530',
        color: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2),
        '& h2': {
            fontSize: '1.5rem',
            fontWeight: 500
        }
    },
    dialogContent: {
        backgroundColor: '#1e1e28',
        color: '#fff',
        padding: theme.spacing(3),
        minWidth: 550,
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto'
    },
    closeButton: {
        color: '#aaa',
        '&:hover': {
            color: '#fff',
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
        }
    },
    streamCard: {
        backgroundColor: '#252530',
        marginBottom: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden'
    },
    streamHeader: {
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        padding: theme.spacing(1, 2),
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
    },
    streamContent: {
        padding: theme.spacing(2)
    },
    streamTitle: {
        color: '#fff',
        fontWeight: 500,
        marginBottom: theme.spacing(1)
    },
    streamDetail: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        padding: theme.spacing(0.5, 0),
        '&:last-child': {
            borderBottom: 'none'
        }
    },
    streamDetailItem: {
        padding: theme.spacing(1, 0),
        borderBottom: '1px dashed rgba(255, 255, 255, 0.05)',
        '&:last-child': {
            borderBottom: 'none'
        }
    },
    streamLabel: {
        color: '#9b9b9b',
        fontSize: '0.875rem'
    },
    streamValue: {
        color: '#fff',
        fontSize: '0.875rem',
        fontWeight: 500
    },
    streamStatsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginTop: theme.spacing(1.5)
    },
    streamStatItem: {
        backgroundColor: '#1e1e28',
        padding: theme.spacing(1),
        minWidth: '100px',
        flex: '1 1 auto',
        borderRadius: theme.shape.borderRadius,
        textAlign: 'center'
    },
    streamStatValue: {
        color: theme.palette.primary.main,
        fontWeight: 'bold',
        fontSize: '1.2rem'
    },
    streamStatLabel: {
        color: '#9b9b9b',
        fontSize: '0.75rem',
        marginTop: '4px',
        textTransform: 'uppercase'
    },
    noDataMessage: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: '#888'
    },
    // 添加日期筛选下拉列表的样式
    dateSelect: {
        backgroundColor: '#252530',
        '& .MuiOutlinedInput-input': {
            padding: '14px',
            color: '#fff',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#444',
        },
        '& .MuiSelect-icon': {
            color: '#fff',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#666',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
    },
    // 修改 syncButton 的样式定义
    syncButton: {
        marginLeft: theme.spacing(1.5),
        background: 'linear-gradient(45deg, #00897B 30%, #009688 90%)',  // 添加渐变背景
        color: 'white',
        padding: '10px 24px',  // 增加水平内边距
        minWidth: 150,
        height: 56,  // 与搜索框保持相同高度
        fontWeight: 500,  // 加粗字体
        letterSpacing: '0.5px',  // 增加字母间距
        boxShadow: '0 3px 10px rgba(0, 150, 136, 0.4)',
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',  // 平滑过渡效果
        borderRadius: 8,  // 圆角边框
        '&:hover': {
            background: 'linear-gradient(45deg, #00796B 30%, #00897B 90%)',  // 悬停时的渐变变化
            boxShadow: '0 5px 15px rgba(0, 150, 136, 0.6)',
            transform: 'translateY(-2px)',  // 悬停时微微上浮
        },
        '& .MuiButton-startIcon': {
            marginRight: 12,  // 增加图标与文字间距
        },
        '&.Mui-disabled': {
            background: 'linear-gradient(45deg, #B2DFDB 30%, #B2DFDB 90%)',  // 禁用状态使用淡色渐变
            color: 'rgba(255, 255, 255, 0.6)',
        },
    },

    dialogButton: {
        marginTop: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    datePicker: {
        width: '100%',
        marginBottom: theme.spacing(2),
        '& .MuiInputBase-input': {
            color: '#fff',
        },
        '& .MuiInputLabel-root': {
            color: '#aaa',
        },
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#252530',
            '& fieldset': {
                borderColor: '#444',
            },
            '&:hover fieldset': {
                borderColor: '#666',
            },
        },
        '& .MuiIconButton-root': {
            color: '#fff',
        },
    },
    // 添加操作按钮的容器样式
    actionButtonsContainer: {
        marginBottom: theme.spacing(3),
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    // 同时优化批量操作按钮，使设计一致
    actionButton: {
        flex: '1 1 250px',
        padding: theme.spacing(2.5),  // 增加内边距
        borderRadius: 12,  // 增加圆角
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: 'none',  // 移除边框
        overflow: 'hidden',  // 确保渐变效果不超出边界
        position: 'relative',  // 为悬停效果做准备
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.25)',
        },
        '&::after': {  // 添加微妙的光泽效果
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
        },
        '&:hover::after': {
            opacity: 1,
        },
    },
    syncButtonAll: {
        background: 'linear-gradient(45deg, #1565C0 30%, #2196F3 90%)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(45deg, #0D47A1 30%, #1565C0 90%)',
        },
    },
    endButton: {
        background: 'linear-gradient(45deg, #FF8F00 30%, #FFC107 90%)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(45deg, #F57C00 30%, #FF8F00 90%)',
        },
    },
    checkoutButton: {
        background: 'linear-gradient(45deg, #2E7D32 30%, #4CAF50 90%)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(45deg, #1B5E20 30%, #2E7D32 90%)',
        },
    },
    buttonIcon: {
        marginRight: theme.spacing(2.5),  // 增加图标与文字间距
        fontSize: '2rem',  // 增大图标尺寸
        filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.2))',  // 为图标添加阴影
    },
    buttonContent: {
        textAlign: 'left',
    },
    buttonTitle: {
        fontWeight: 'bold',
        marginBottom: 4,
    },
    buttonDescription: {
        fontSize: '0.75rem',
        opacity: 0.9,
    },
    alertContainer: {
        marginBottom: theme.spacing(3),
    },
    // 在 useStyles 定义中添加重置按钮样式
    resetButton: {
        marginLeft: theme.spacing(1.5),
        background: 'linear-gradient(45deg, #d32f2f 30%, #f44336 90%)',
        color: 'white',
        padding: '10px 24px',
        minWidth: 150,
        height: 56,
        fontWeight: 500,
        letterSpacing: '0.5px',
        boxShadow: '0 3px 10px rgba(244, 67, 54, 0.4)',
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
        borderRadius: 8,
        '&:hover': {
            background: 'linear-gradient(45deg, #b71c1c 30%, #d32f2f 90%)',
            boxShadow: '0 5px 15px rgba(244, 67, 54, 0.6)',
            transform: 'translateY(-2px)',
        },
        '& .MuiButton-startIcon': {
            marginRight: 12,
        },
        '&.Mui-disabled': {
            background: 'linear-gradient(45deg, #ffcdd2 30%, #ffcdd2 90%)',
            color: 'rgba(255, 255, 255, 0.6)',
        },
    },
}));

// 修改 TableHeaderCell 样式，确保内容不换行
const TableHeaderCell = withStyles(() => ({
    head: {
        color: '#9b9b9b',
        fontWeight: '500',
        letterSpacing: '1.2px',
        fontSize: '14px',
        borderBottom: '2px solid #333',
        padding: '16px 16px', // 减少左右内边距
        backgroundColor: '#1e1e28',
        whiteSpace: 'nowrap', // 强制不换行
        overflow: 'hidden',
        textOverflow: 'ellipsis', // 超出部分显示省略号
    },
}))(TableCell);

function QuestLeaderboard(props) {
    const classes = useStyles();
    const [questId, setQuestId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [leaderboard, setLeaderboard] = useState([]);
    const [questInfo, setQuestInfo] = useState(null);
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [participantCount, setParticipantCount] = useState(0);
    const [totalStreamingHours, setTotalStreamingHours] = useState(0);
    const [totalWatchTime, setTotalWatchTime] = useState(0);

    // 添加日期筛选相关状态
    const [openSyncDialog, setOpenSyncDialog] = useState(false);
    const [syncStartDate, setSyncStartDate] = useState(null);
    const [syncEndDate, setSyncEndDate] = useState(null);
    const [resetData, setResetData] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncSuccess, setSyncSuccess] = useState('');
    const [syncError, setSyncError] = useState('');
    const [syncStats, setSyncStats] = useState(null);
    const [isAddingStream, setIsAddingStream] = useState(false);
    const [addStreamFormData, setAddStreamFormData] = useState({
        title: '',
        gameName: '',
        streamTimeFormat: 0,
        cumulativeWatchTime: 0,
        totalRewardPoints: 0,
        startTime: new Date(),
        endTime: new Date()
    });

    // 在组件内部状态区域添加
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [resetReason, setResetReason] = useState('');
    const [isResetting, setIsResetting] = useState(false);
    const [resetOptions, setResetOptions] = useState({
        resetQuestStats: true,
        resetUserQuestStats: true,
        resetLeaderboard: true,
        resetLiveStreamData: true
    });

    // 添加处理函数
    const handleOpenResetDialog = () => {
        if (!questId || !questInfo) {
            setError('请先查询任务再执行重置操作');
            return;
        }

        setOpenResetDialog(true);
        setResetReason('');
        setResetOptions({
            resetQuestStats: true,
            resetUserQuestStats: true,
            resetLeaderboard: true,
            resetLiveStreamData: true
        });
    };

    const handleCloseResetDialog = () => {
        setOpenResetDialog(false);
    };

    const handleResetOptionChange = (event) => {
        const { name, checked } = event.target;
        setResetOptions(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleResetReasonChange = (e) => {
        setResetReason(e.target.value);
    };

    const handleResetData = async () => {
        if (!questId || !resetReason.trim()) {
            return;
        }

        try {
            setIsResetting(true);
            const { token } = props.auth;
            const config = {
                headers: {
                    Authorization: `JWT ${token}`,
                    'Content-Type': 'application/json'
                }
            };

            // 准备请求数据
            const requestData = {
                questId: questId,
                reason: resetReason.trim(),
                resetOptions: resetOptions
            };

            // 调用重置API
            const response = await axios.post('/api/v1/user/test-reset-quest-data', requestData, config);

            if (response.data && response.data.code === 'QUEST_RESET_SUCCESS') {
                setActionAlert({
                    show: true,
                    severity: 'success',
                    message: `任务数据重置成功！任务ID: ${response.data.quest}, 执行时间: ${(response.data.executionTime / 1000).toFixed(2)}秒`
                });

                // 关闭对话框并重新加载数据
                handleCloseResetDialog();
                fetchLeaderboard();
            } else {
                throw new Error(response.data?.message || '重置数据返回了意外的响应格式');
            }
        } catch (error) {
            console.error('重置任务数据错误:', error);

            setActionAlert({
                show: true,
                severity: 'error',
                message: `重置失败: ${error.response?.data?.message || error.message || '未知错误'}`
            });
        } finally {
            setIsResetting(false);
            // 5秒后自动隐藏提示
            setTimeout(() => setActionAlert(prev => ({ ...prev, show: false })), 10000);
        }
    };

    // 添加重置对话框组件
    const renderResetDialog = () => {
        return (
            <Dialog
                open={openResetDialog}
                onClose={handleCloseResetDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        backgroundColor: '#1e1e28',
                        borderRadius: 8,
                    },
                }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    <Box display="flex" alignItems="center">
                        <WarningIcon style={{ color: '#f44336', marginRight: 12 }} />
                        <Typography variant="h6">
                            重置任务数据
                        </Typography>
                    </Box>
                    <IconButton
                        className={classes.closeButton}
                        onClick={handleCloseResetDialog}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Box mb={3} p={2} bgcolor="rgba(244, 67, 54, 0.05)" borderRadius={1} border="1px solid rgba(244, 67, 54, 0.2)">
                        <Typography variant="subtitle1" style={{ color: '#f44336', fontWeight: 500, marginBottom: 8 }}>
                            警告：此操作不可逆
                        </Typography>
                        <Typography variant="body2" style={{ color: '#ccc' }}>
                            您将重置任务 <strong style={{ color: '#2196f3' }}>{questInfo?.title || questId}</strong> 的全部数据。
                            此操作会清除所选数据类型，并且<strong style={{ color: '#f44336' }}>无法恢复</strong>。请确认您了解此操作的后果。
                        </Typography>
                    </Box>

                    <Typography variant="subtitle2" gutterBottom style={{ color: '#aaa' }}>
                        重置选项
                    </Typography>
                    <Box mb={3}>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={resetOptions.resetQuestStats}
                                        onChange={handleResetOptionChange}
                                        name="resetQuestStats"
                                        color="primary"
                                    />
                                }
                                label="重置任务统计数据"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={resetOptions.resetUserQuestStats}
                                        onChange={handleResetOptionChange}
                                        name="resetUserQuestStats"
                                        color="primary"
                                    />
                                }
                                label="重置用户任务数据"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={resetOptions.resetLeaderboard}
                                        onChange={handleResetOptionChange}
                                        name="resetLeaderboard"
                                        color="primary"
                                    />
                                }
                                label="重置排行榜数据"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={resetOptions.resetLiveStreamData}
                                        onChange={handleResetOptionChange}
                                        name="resetLiveStreamData"
                                        color="primary"
                                    />
                                }
                                label="重置直播数据"
                            />
                        </FormGroup>
                    </Box>

                    <TextField
                        label="重置原因"
                        multiline
                        rows={3}
                        fullWidth
                        variant="outlined"
                        value={resetReason}
                        onChange={handleResetReasonChange}
                        placeholder="请输入重置此任务数据的原因（必填）"
                        InputLabelProps={{ shrink: true }}
                        style={{ marginBottom: 16 }}
                        required
                        error={isResetting && !resetReason.trim()}
                        helperText={isResetting && !resetReason.trim() ? "重置原因不能为空" : ""}
                    />
                </DialogContent>
                <DialogActions style={{ backgroundColor: '#1e1e28', padding: '16px 24px' }}>
                    <Button
                        onClick={handleCloseResetDialog}
                        color="default"
                        className={classes.dialogButton}
                        disabled={isResetting}
                    >
                        取消
                    </Button>
                    <Button
                        onClick={handleResetData}
                        color="secondary"
                        variant="contained"
                        className={classes.dialogButton}
                        disabled={isResetting || !resetReason.trim()}
                        startIcon={isResetting ? <CircularProgress size={20} /> : <DeleteIcon />}
                    >
                        {isResetting ? '重置中...' : '确认重置'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // 添加用于处理操作消息的状态
    const [actionAlert, setActionAlert] = useState({ show: false, severity: 'success', message: '' });
    const [isProcessing, setIsProcessing] = useState({
        syncYesterday: false,
        endQuests: false,
        checkoutQuests: false
    });
    // 为下拉菜单添加常量，定义在组件顶层
    const ALL_STREAMS = -1;
    // const ALL_DATES = 'all';

    // // 打开新增直播对话框
    // const handleOpenAddStream = () => {
    //     // 设置默认开始时间为当天上午10点
    //     const defaultStartTime = new Date();
    //     defaultStartTime.setHours(10, 0, 0, 0);

    //     // 设置默认结束时间为当天下午6点
    //     const defaultEndTime = new Date();
    //     defaultEndTime.setHours(18, 0, 0, 0);

    //     setAddStreamFormData({
    //         title: '',
    //         gameName: questInfo?.game?.name || '',
    //         streamTimeFormat: 120, // 默认2小时
    //         cumulativeWatchTime: 0,
    //         bitsRewardPoints: 0,
    //         startTime: defaultStartTime,
    //         endTime: defaultEndTime
    //     });
    //     setIsAddingStream(true);
    // };

    // 处理同步所有任务昨日排行榜
    const handleSyncAllQuestsYesterday = async () => {
        try {
            setIsProcessing(prev => ({ ...prev, syncYesterday: true }));
            const { token } = props.auth;
            const config = { headers: { Authorization: `JWT ${token}` } };

            const response = await axios.get('/api/v1/user/test-all-quests-rank-yesterday', config);

            // 修改判断条件，检查正确的成功码
            if (response.data && response.data.code === 'QUEST_LEADERBOARD_UPDATED') {
                setActionAlert({
                    show: true,
                    severity: 'success',
                    message: `成功同步所有任务昨日排行榜！处理任务数: ${response.data.statistics.processedQuests}, 成功: ${response.data.statistics.successQuests}`
                });
            } else {
                throw new Error(response.data?.message || '同步失败，未知错误');
            }
        } catch (error) {
            console.error('同步所有任务昨日排行榜出错:', error);
            setActionAlert({
                show: true,
                severity: 'error',
                message: `同步失败: ${error.response?.data?.message || error.message || '未知错误'}`
            });
        } finally {
            setIsProcessing(prev => ({ ...prev, syncYesterday: false }));
            // 5秒后自动隐藏提示
            setTimeout(() => setActionAlert(prev => ({ ...prev, show: false })), 10000);
        }
    };

    // 处理结束当前24小时所有任务
    const handleEndAllQuests = async () => {
        try {
            setIsProcessing(prev => ({ ...prev, endQuests: true }));
            const { token } = props.auth;
            const config = { headers: { Authorization: `JWT ${token}` } };

            const response = await axios.get('/api/v1/user/test-all-quests-auto-end', config);

            // 修改判断条件，检查正确的成功码
            if (response.data && response.data.code === 'QUEST_AUTO_CHECKOUT_COMPLETED') {
                setActionAlert({
                    show: true,
                    severity: 'success',
                    message: `成功结束所有24小时任务！处理任务数: ${response.data.statistics.processedQuests}`
                });
            } else {
                throw new Error(response.data?.message || '操作失败，未知错误');
            }
        } catch (error) {
            console.error('结束所有任务出错:', error);
            setActionAlert({
                show: true,
                severity: 'error',
                message: `操作失败: ${error.response?.data?.message || error.message || '未知错误'}`
            });
        } finally {
            setIsProcessing(prev => ({ ...prev, endQuests: false }));
            setTimeout(() => setActionAlert(prev => ({ ...prev, show: false })), 5000);
        }
    };

    // 处理自动结算所有24小时以内的内容
    const handleCheckoutAllQuests = async () => {
        try {
            setIsProcessing(prev => ({ ...prev, checkoutQuests: true }));
            const { token } = props.auth;
            const config = { headers: { Authorization: `JWT ${token}` } };

            const response = await axios.get('/api/v1/user/test-all-quests-auto-checkout', config);

            // 修改判断条件，检查正确的成功码
            if (response.data && response.data.code === 'QUEST_AUTO_CHECKOUT_COMPLETED') {
                setActionAlert({
                    show: true,
                    severity: 'success',
                    message: `成功执行任务自动结算操作！处理任务数: ${response.data.statistics.processedQuests}，总耗时: ${(response.data.statistics.totalExecutionTime / 1000).toFixed(2)}秒`
                });
            } else {
                throw new Error(response.data?.message || '结算失败，未知错误');
            }
        } catch (error) {
            console.error('结算所有任务出错:', error);
            setActionAlert({
                show: true,
                severity: 'error',
                message: `结算失败: ${error.response?.data?.message || error.message || '未知错误'}`
            });
        } finally {
            setIsProcessing(prev => ({ ...prev, checkoutQuests: false }));
            setTimeout(() => setActionAlert(prev => ({ ...prev, show: false })), 5000);
        }
    };

    // 关闭新增直播对话框
    const handleCloseAddStream = () => {
        setIsAddingStream(false);
    };

    // 处理新增直播表单变化
    const handleAddStreamFormChange = (event) => {
        const { name, value } = event.target;
        setAddStreamFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 处理日期时间变化
    const handleDateTimeChange = (name, value) => {
        setAddStreamFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 提交新增直播数据
    const handleSubmitNewStream = async () => {
        if (!selectedUser || !questId) return;

        try {
            setIsSaving(true);

            // 准备直播数据
            const newStreamData = {
                userId: selectedUser.user.id,
                twitchUserId: selectedUser.user.connectedAccounts?.twitch?.id || '',
                twitchUsername: selectedUser.user.connectedAccounts?.twitch?.username || selectedUser.twitchName || '',
                title: addStreamFormData.title,
                gameName: addStreamFormData.gameName,
                streamTimeFormat: parseInt(addStreamFormData.streamTimeFormat),
                cumulativeWatchTime: parseInt(addStreamFormData.cumulativeWatchTime),
                totalRewardPoints: parseInt(addStreamFormData.totalRewardPoints),
                startTime: addStreamFormData.startTime,
                endTime: addStreamFormData.endTime,
                dataSource: 'manual'
            };

            // 请求参数
            const requestData = {
                questId: questId,
                startTime: moment(addStreamFormData.startTime).startOf('day').toISOString(),
                endTime: moment(addStreamFormData.endTime).endOf('day').toISOString(),
                resetData: false,
                isManualModify: true,
                liveStreamData: newStreamData
            };

            // 调用API
            const { token } = props.auth;
            const config = {
                headers: {
                    Authorization: `JWT ${token}`,
                    'Content-Type': 'application/json'
                }
            };

            const response = await axios.post('/api/v1/user/test-update-quest-rank', requestData, config);

            if (response.data && response.data.code === 'LEADERBOARD_CALCULATION_COMPLETED') {
                // 显示成功对话框
                setDialogLevel('success');
                setDialogTitle('直播数据已添加');
                setDialogContent('新直播数据已成功添加并重新计算排行榜');
                setDialogOpen(true);

                // 重新加载排行榜数据
                fetchLeaderboard();

                // 关闭对话框
                handleCloseAddStream();
            } else {
                throw new Error(response.data?.message || '添加直播数据时发生错误');
            }
        } catch (error) {
            console.error('添加直播数据时出错:', error);

            setDialogLevel('error');
            setDialogTitle('添加失败');
            setDialogContent(error.response?.data?.message || '无法添加直播数据，请稍后再试');
            setDialogOpen(true);
        } finally {
            setIsSaving(false);
        }
    };

    // 处理详情对话框打开
    const handleOpenDetail = (user) => {
        setSelectedUser(user);
        setSelectedStreamIndex(ALL_STREAMS); // 默认显示全部直播
        setIsEditing(false);
        setOpenDetailDialog(true);

        // 在组件打开时处理日期数据
        if (user.liveStreams && user.liveStreams.length > 0) {
            // 提取所有直播的日期
            const dateMap = {};
            user.liveStreams.forEach(stream => {
                if (stream.startTime) {
                    const streamDate = moment(stream.startTime).format('YYYY-MM-DD');
                    if (!dateMap[streamDate]) {
                        dateMap[streamDate] = [];
                    }
                    dateMap[streamDate].push(stream);
                }
            });

            // 转换为数组并排序（最新日期在前）
            const sortedDates = Object.keys(dateMap).sort((a, b) =>
                moment(b).diff(moment(a))
            );

            setStreamDates(sortedDates);

            // 确定默认选择的日期：最接近当前日期的一天
            let defaultDate = '';
            const now = moment();
            const questEndDate = questInfo?.endDateTime ? moment(questInfo.endDateTime) : null;

            // 如果任务已结束，取离结束时间最近的日期，否则取离当前时间最近的日期
            const referenceDate = (questEndDate && now.isAfter(questEndDate)) ? questEndDate : now;

            if (sortedDates.length > 0) {
                let minDiff = Infinity;
                sortedDates.forEach(date => {
                    const diff = Math.abs(moment(date).diff(referenceDate, 'days'));
                    if (diff < minDiff) {
                        minDiff = diff;
                        defaultDate = date;
                    }
                });

                setSelectedDate(defaultDate);

                // 设置默认日期的直播列表
                setFilteredStreams(dateMap[defaultDate] || []);
            } else {
                // 没有有效日期时显示所有直播
                setFilteredStreams(user.liveStreams);
            }
        }
    };

    // 处理同步按钮点击
    const handleOpenSyncDialog = () => {
        if (!questInfo) return;

        // 设置默认开始时间为任务开始时间
        const startDate = new Date(questInfo.startDateTime);
        setSyncStartDate(startDate);

        // 设置默认结束时间为当前时间和任务结束时间中较早的那个
        const now = new Date();
        const endDate = new Date(questInfo.endDateTime);
        setSyncEndDate(now < endDate ? now : endDate);

        // 如果时间范围完全等于任务的开始和结束时间，则重置数据
        const isFullTimeRange =
            startDate.getTime() === new Date(questInfo.startDateTime).getTime()

        console.log('isFullTimeRange=====================================>:', isFullTimeRange);
        setResetData(isFullTimeRange);

        // 重置其他状态
        setSyncSuccess('');
        setSyncError('');
        setSyncStats(null);

        // 打开对话框
        setOpenSyncDialog(true);
    };

    // 添加一个新的辅助函数来检查日期范围并设置resetData
    const updateResetDataBasedOnDateRange = (start, end) => {
        if (!questInfo || !start || !end) return;

        const isFullTimeRange =
            moment(start).startOf('day').isSame(moment(questInfo.startDateTime).startOf('day')) &&
            moment(end).startOf('day').isSame(moment(questInfo.endDateTime).startOf('day'));

        setResetData(isFullTimeRange);
    };

    // 关闭同步对话框
    const handleCloseSyncDialog = () => {
        setOpenSyncDialog(false);
    };

    // 处理同步数据操作
    const handleSyncData = async () => {
        if (!questId || !syncStartDate || !syncEndDate) return;

        // 检查日期有效性
        if (syncStartDate > syncEndDate) {
            setSyncError('开始时间不能晚于结束时间');
            return;
        }

        setIsSyncing(true);
        setSyncError('');
        setSyncSuccess('');
        setSyncStats(null);

        try {
            const { token } = props.auth;
            const config = {
                headers: {
                    Authorization: `JWT ${token}`,
                    'Content-Type': 'application/json',
                }
            };

            // 准备请求数据
            const requestData = {
                questId: questId,
                startTime: syncStartDate.toISOString(),
                endTime: syncEndDate.toISOString(),
                resetData: resetData,
                isManualModify: false
            };

            // 调用同步API
            const response = await axios.post('/api/v1/user/test-update-quest-rank', requestData, config);

            if (response.data && response.data.code === 'LEADERBOARD_CALCULATION_COMPLETED') {
                setSyncSuccess('排行榜数据同步成功!');
                setSyncStats(response.data.statistics);

                // 重新加载排行榜数据
                fetchLeaderboard();

                // 3秒后自动关闭对话框
                setTimeout(() => {
                    handleCloseSyncDialog();
                }, 3000);
            } else {
                throw new Error(response.data?.message || '同步数据返回了意外的响应格式');
            }
        } catch (err) {
            console.error('同步排行榜数据错误:', err);

            // 处理错误消息
            let errorMessage = '同步排行榜数据失败，请稍后重试';
            if (err.response && err.response.data) {
                errorMessage = err.response.data.message || errorMessage;
            } else if (err.message) {
                errorMessage = err.message;
            }

            setSyncError(errorMessage);
        } finally {
            setIsSyncing(false);
        }
    };

    // 处理详情对话框关闭
    const handleCloseDetail = () => {
        setOpenDetailDialog(false);
        setSelectedUser(null);
        setIsEditing(false);
        // 重置为 ALL_STREAMS
        setSelectedStreamIndex(ALL_STREAMS);
    };

    // 直播详情相关状态
    const [openDetailDialog, setOpenDetailDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    // 修改默认值为 ALL_STREAMS
    const [selectedStreamIndex, setSelectedStreamIndex] = useState(ALL_STREAMS);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editFormData, setEditFormData] = useState({
        totalRewardPoints: 0,
        streamTimeFormat: 0,
        cumulativeWatchTime: 0
    });

    // 日期筛选相关状态
    const [streamDates, setStreamDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState('all');
    const [filteredStreams, setFilteredStreams] = useState([]);

    // 添加对话框相关状态
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogLevel, setDialogLevel] = useState('success');
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogContent, setDialogContent] = useState('');

    // 处理对话框关闭
    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    // 处理日期选择变化
    const handleDateChange = (event) => {
        const date = event.target.value;
        setSelectedDate(date);

        if (!selectedUser || !selectedUser.liveStreams) return;

        if (date === 'all') {
            // 选择"全部日期"时显示所有直播
            setFilteredStreams(selectedUser.liveStreams);
        } else {
            // 筛选指定日期的直播
            const filtered = selectedUser.liveStreams.filter(stream =>
                stream.startTime && moment(stream.startTime).format('YYYY-MM-DD') === date
            );
            setFilteredStreams(filtered);
        }

        // 重置其他状态
        setSelectedStreamIndex(ALL_STREAMS);
        setIsEditing(false);
    };

    // 处理直播选择变化
    // const handleStreamChange = (event) => {
    //     setSelectedStreamIndex(parseInt(event.target.value));
    //     setIsEditing(false);
    // };

    // // 开始编辑直播数据
    // const handleEditClick = () => {
    //     if (!selectedUser || !selectedUser.liveStreams || selectedUser.liveStreams.length === 0) return;

    //     const currentStream = selectedUser.liveStreams[selectedStreamIndex];
    //     setEditFormData({
    //         bitsRewardPoints: currentStream.bitsRewardPoints || 0,
    //         streamTimeFormat: currentStream.streamTimeFormat || 0,
    //         cumulativeWatchTime: currentStream.cumulativeWatchTime || 0
    //     });
    //     setIsEditing(true);
    // };

    // 取消编辑
    const handleCancelEdit = () => {
        setIsEditing(false);
    };

    // 处理表单数据变化
    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleQuestIdChange = (e) => {
        setQuestId(e.target.value);
    };

    const fetchLeaderboard = async () => {
        if (!questId) {
            setError('请输入有效的任务ID');
            return;
        }

        setIsLoading(true);
        setError('');
        setHasSearched(true);
        setQuestInfo(null);
        setLeaderboard([]);

        try {
            const { token } = props.auth;

            // 创建请求配置
            const config = {
                headers: {
                    Authorization: `JWT ${token}`,
                    'Content-Type': 'application/json',
                }
            };

            // 查询任务基本信息
            const questResponse = await axios.get(`/api/v1/admin/quest/${questId}/tiered`, config);

            // 检查响应中是否包含数据
            if (questResponse.data && questResponse.data.code === "QUEST_TIERED_ACTIVITY") {
                // 使用API直接返回的排行榜数据
                if (questResponse.data.leaderboard && questResponse.data.leaderboard.length > 0) {
                    setLeaderboard(questResponse.data.leaderboard);
                } else {
                    setLeaderboard([]);
                    setError('该任务暂无排行榜数据');
                }

                // 设置任务信息
                setQuestInfo({
                    ...questResponse.data.quest,
                    participantsCount: (questResponse.data.userQuests || []).length
                });

                // 计算统计数据
                if (questResponse.data.userQuests) {
                    setParticipantCount(questResponse.data.userQuests.length);

                    // 计算总直播时间和总观看时间
                    let totalHours = 0;
                    let totalWatch = 0;

                    questResponse.data.userQuests.forEach(uq => {
                        totalHours += (uq.liveStreamingTime || 0) / 60; // 转换为小时
                        totalWatch += (uq.viewingTime || 0);
                    });

                    setTotalStreamingHours(Math.round(totalHours));
                    setTotalWatchTime(totalWatch);
                }
            } else {
                throw new Error(questResponse.data?.message || '未找到任务信息或数据格式错误');
            }
        } catch (err) {
            console.error('查询排行榜错误:', err);
            const errorMessage = err.response?.data?.message || err.message || '查询排行榜失败，请稍后重试';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            fetchLeaderboard();
        }
    };

    // 获取任务状态信息
    const getQuestStatusInfo = (quest) => {
        if (!quest) return { statusText: "-", statusClass: "" };

        const isEnded = isQuestOver(quest);
        const isPaid = isQuestPaid(quest);

        if (!isEnded) {
            return { statusText: "活跃中", statusClass: classes.activeStatus };
        } else if (isPaid) {
            return { statusText: "已支付", statusClass: classes.paidStatus };
        } else {
            return { statusText: "已结束", statusClass: classes.inactiveStatus };
        }
    };

    // 添加在组件内合适位置

    // 渲染新增直播对话框
    const renderAddStreamDialog = () => {
        if (!selectedUser) return null;

        return (
            <Dialog
                open={isAddingStream}
                onClose={handleCloseAddStream}
                maxWidth="md"
                PaperProps={{
                    style: {
                        backgroundColor: '#1e1e28',
                        borderRadius: 8
                    }
                }}
            >
                <DialogTitle disableTypography className={classes.dialogTitle}>
                    <Typography variant="h6">
                        为 {selectedUser.user.displayName || selectedUser.user.username || '未知用户'} 添加新直播
                    </Typography>
                    <IconButton aria-label="close" className={classes.closeButton} onClick={handleCloseAddStream}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Typography variant="body2" color="textSecondary" paragraph>
                        添加直播数据将影响该用户在排行榜中的位置和分数。保存后系统将重新计算排名。
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="直播标题"
                                name="title"
                                fullWidth
                                variant="outlined"
                                value={addStreamFormData.title}
                                onChange={handleAddStreamFormChange}
                                InputLabelProps={{ shrink: true }}
                                placeholder="例如：绝地求生精彩游戏"
                                style={{ marginBottom: 16 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="游戏名称"
                                name="gameName"
                                fullWidth
                                variant="outlined"
                                value={addStreamFormData.gameName}
                                onChange={handleAddStreamFormChange}
                                InputLabelProps={{ shrink: true }}
                                placeholder="例如：PUBG"
                                style={{ marginBottom: 16 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="开始时间"
                                name="startTime"
                                type="datetime-local"
                                fullWidth
                                variant="outlined"
                                value={moment(addStreamFormData.startTime).format('YYYY-MM-DDTHH:mm')}
                                onChange={(e) => handleDateTimeChange('startTime', new Date(e.target.value))}
                                InputLabelProps={{ shrink: true }}
                                style={{ marginBottom: 16 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="结束时间"
                                name="endTime"
                                type="datetime-local"
                                fullWidth
                                variant="outlined"
                                value={moment(addStreamFormData.endTime).format('YYYY-MM-DDTHH:mm')}
                                onChange={(e) => handleDateTimeChange('endTime', new Date(e.target.value))}
                                InputLabelProps={{ shrink: true }}
                                style={{ marginBottom: 16 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="直播时长(分钟)"
                                name="streamTimeFormat"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={addStreamFormData.streamTimeFormat}
                                onChange={handleAddStreamFormChange}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">分钟</InputAdornment>,
                                }}
                                style={{ marginBottom: 16 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="累计观看时长(分钟)"
                                name="cumulativeWatchTime"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={addStreamFormData.cumulativeWatchTime}
                                onChange={handleAddStreamFormChange}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">分钟</InputAdornment>,
                                }}
                                style={{ marginBottom: 16 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="打赏奖励"
                                name="bitsRewardPoints"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={addStreamFormData.bitsRewardPoints}
                                onChange={handleAddStreamFormChange}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">点</InputAdornment>,
                                    style: { color: '#ff9a2e' }
                                }}
                                style={{ marginBottom: 16 }}
                            />
                        </Grid>
                    </Grid>

                    <Box mt={2}>
                        <Typography variant="caption" color="textSecondary">
                            <InfoOutlinedIcon style={{ fontSize: 16, verticalAlign: 'text-bottom', marginRight: 4 }} />
                            添加的直播数据将立即影响该用户在任务中的计算分数和排名位置
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions style={{ backgroundColor: '#1e1e28', padding: '16px 24px' }}>
                    <Button
                        onClick={handleCloseAddStream}
                        color="default"
                        className={classes.dialogButton}
                        disabled={isSaving}
                    >
                        取消
                    </Button>
                    <Button
                        onClick={handleSubmitNewStream}
                        color="primary"
                        variant="contained"
                        className={classes.dialogButton}
                        disabled={isSaving}
                        startIcon={isSaving ? <CircularProgress size={20} /> : null}
                    >
                        {isSaving ? '保存中...' : '保存'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // 修改 renderStreamDetailDialog 函数中的代码
    const renderStreamDetailDialog = () => {
        if (!selectedUser) return null;

        const userInfo = selectedUser.user || {};
        const hasLiveStreams = selectedUser.liveStreams && selectedUser.liveStreams.length > 0;

        // 为下拉菜单添加常量，使 -1 表示 "ALL" 选项
        const ALL_STREAMS = -1;

        // 处理直播选择变化
        const handleStreamChange = (event) => {
            const value = parseInt(event.target.value);
            setSelectedStreamIndex(value);
            setIsEditing(false); // 切换直播数据时取消编辑状态
        };

        // 开始编辑直播数据
        const handleEditClick = () => {
            if (!hasLiveStreams || selectedStreamIndex === ALL_STREAMS) return;

            const currentStream = selectedUser.liveStreams[selectedStreamIndex];
            setEditFormData({
                totalRewardPoints: currentStream.totalRewardPoints || 0,
                streamTimeFormat: currentStream.streamTimeFormat || 0,
                cumulativeWatchTime: currentStream.cumulativeWatchTime || 0
            });
            setIsEditing(true);
        };

        // 修改保存编辑直播数据的函数
        const handleSaveStreamData = async () => {
            if (!selectedUser || !selectedUser.liveStreams || selectedStreamIndex === ALL_STREAMS) return;

            const streamToUpdate = selectedUser.liveStreams[selectedStreamIndex];

            try {
                setIsSaving(true);

                // 准备直播数据
                const updatedStreamData = {
                    id: streamToUpdate.id,
                    totalRewardPoints: parseInt(editFormData.totalRewardPoints),
                    streamTimeFormat: parseInt(editFormData.streamTimeFormat),
                    cumulativeWatchTime: parseInt(editFormData.cumulativeWatchTime),
                    dataSource: 'manual_modification'
                };

                // 请求参数
                const requestData = {
                    questId: questId,
                    startTime: moment(streamToUpdate.startTime).startOf('day').toISOString(),
                    endTime: moment(streamToUpdate.endTime).endOf('day').toISOString(),
                    resetData: true,
                    isManualModify: true,
                    liveStreamData: updatedStreamData
                };

                // 调用API
                const { token } = props.auth;
                const config = {
                    headers: {
                        Authorization: `JWT ${token}`,
                        'Content-Type': 'application/json'
                    }
                };

                const response = await axios.post('/api/v1/user/test-update-quest-rank', requestData, config);

                if (response.data && response.data.code === 'LEADERBOARD_CALCULATION_COMPLETED') {
                    // 显示成功对话框
                    setDialogLevel('success');
                    setDialogTitle('直播数据已更新');
                    setDialogContent('直播数据已成功保存并重新计算排行榜');
                    setDialogOpen(true);

                    // 更新本地数据
                    const updatedLiveStreams = [...selectedUser.liveStreams];
                    updatedLiveStreams[selectedStreamIndex] = {
                        ...streamToUpdate,
                        ...editFormData
                    };

                    selectedUser.liveStreams = updatedLiveStreams;

                    // 同时更新筛选后的直播列表
                    if (selectedDate !== 'all') {
                        setFilteredStreams(selectedUser.liveStreams.filter(stream =>
                            stream.startTime && moment(stream.startTime).format('YYYY-MM-DD') === selectedDate
                        ));
                    } else {
                        setFilteredStreams(selectedUser.liveStreams);
                    }

                    // 退出编辑模式
                    setIsEditing(false);

                    // 重新加载排行榜数据
                    fetchLeaderboard();
                } else {
                    throw new Error(response.data?.message || '更新直播数据时发生错误');
                }
            } catch (error) {
                console.error('保存直播数据时出错:', error);

                setDialogLevel('error');
                setDialogTitle('保存失败');
                setDialogContent(error.response?.data?.message || '无法更新直播数据，请稍后再试');
                setDialogOpen(true);
            } finally {
                setIsSaving(false);
            }
        };
        return (
            <Dialog
                open={openDetailDialog}
                onClose={handleCloseDetail}
                maxWidth="lg"
                PaperProps={{
                    style: {
                        backgroundColor: '#1e1e28',
                        borderRadius: 8,
                        width: '900px'
                    }
                }}
            >
                {/* 对话框标题 */}
                <DialogTitle disableTypography className={classes.dialogTitle}>
                    <Box display="flex" alignItems="center">
                        {selectedUser.rank <= 3 && (
                            <span style={{ fontSize: '24px', marginRight: '12px' }}>
                                {selectedUser.rank === 1 ? '🥇' : selectedUser.rank === 2 ? '🥈' : '🥉'}
                            </span>
                        )}
                        <Typography variant="h6">
                            {userInfo.displayName || userInfo.username || '未知用户'} 的直播数据
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                        {/* <Button
                            color="primary"
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleOpenAddStream}
                            style={{ marginRight: 16 }}
                        >
                            添加直播
                        </Button> */}
                        <IconButton aria-label="close" className={classes.closeButton} onClick={handleCloseDetail}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                {/* 对话框内容 */}
                <DialogContent dividers className={classes.dialogContent}>
                    {/* 用户概览信息 */}
                    <Card className={classes.streamCard} elevation={0}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom style={{ color: '#2196f3', marginBottom: 16 }}>
                                用户概览
                            </Typography>
                            <Box display="flex" flexWrap="wrap" justifyContent="space-between">
                                <Box flex="1" minWidth="280px" mr={2}>
                                    <Paper style={{ backgroundColor: 'rgba(33, 150, 243, 0.05)', padding: 16, borderRadius: 8, height: '100%' }}>
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <TrophyIcon style={{ color: '#FFD700', marginRight: 12 }} />
                                            <Typography variant="subtitle1" style={{ color: '#9b9b9b' }}>
                                                排名
                                            </Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center" pl={4}>
                                            <Typography variant="h4" style={{
                                                fontWeight: 'bold',
                                                color: selectedUser.rank === 1 ? '#FFD700' :
                                                    selectedUser.rank === 2 ? '#C0C0C0' :
                                                        selectedUser.rank === 3 ? '#CD7F32' : '#2196f3'
                                            }}>
                                                #{selectedUser.rank}
                                            </Typography>
                                        </Box>
                                        <Divider style={{ margin: '16px 0', backgroundColor: 'rgba(255,255,255,0.1)' }} />
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <SortIcon style={{ color: '#2196f3', marginRight: 12 }} />
                                            <Typography variant="subtitle1" style={{ color: '#9b9b9b' }}>
                                                总分
                                            </Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center" pl={4}>
                                            <Typography variant="h5" style={{ fontWeight: 'bold' }}>
                                                {Math.round(selectedUser.overallScore || 0).toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Box>
                                <Box flex="1" minWidth="280px">
                                    <Paper style={{ backgroundColor: 'rgba(76, 175, 80, 0.05)', padding: 16, borderRadius: 8, height: '100%' }}>
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <MonetizationOnIcon style={{ color: '#4caf50', marginRight: 12 }} />
                                            <Typography variant="subtitle1" style={{ color: '#9b9b9b' }}>
                                                奖励金额
                                            </Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center" pl={4}>
                                            <Typography variant="h5" style={{ fontWeight: 'bold', color: '#4caf50' }}>
                                                ${selectedUser.rewardAmount ? selectedUser.rewardAmount.toFixed(2) : '0.00'}
                                            </Typography>
                                        </Box>
                                        <Divider style={{ margin: '16px 0', backgroundColor: 'rgba(255,255,255,0.1)' }} />
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="#9146FF" style={{ marginRight: 12 }}>
                                                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
                                            </svg>
                                            <Typography variant="subtitle1" style={{ color: '#9b9b9b' }}>
                                                Twitch
                                            </Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center" pl={4}>
                                            <Typography variant="h5" style={{ fontWeight: 'bold', color: '#9146FF' }}>
                                                {userInfo.connectedAccounts?.twitch?.username || selectedUser.twitchName || '-'}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* 直播数据列表 */}
                    {hasLiveStreams ? (
                        <>
                            {/* 直播记录筛选控件 */}
                            <Box mt={3} mb={2} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6" style={{ color: '#2196f3' }}>
                                    直播记录 ({selectedUser.liveStreams.length})
                                </Typography>

                                <Box display="flex" alignItems="center">
                                    {/* 添加日期筛选下拉菜单 */}
                                    <FormControl variant="outlined" size="small" style={{ minWidth: 150, marginRight: 8 }}>
                                        <InputLabel id="stream-date-select-label">日期筛选</InputLabel>
                                        <Select
                                            labelId="stream-date-select-label"
                                            id="stream-date-select"
                                            value={selectedDate}
                                            onChange={handleDateChange}
                                            label="日期筛选"
                                            disabled={isEditing}
                                            style={{
                                                backgroundColor: '#252530',
                                                color: 'white',
                                                borderColor: '#444'
                                            }}
                                        >
                                            <MenuItem value="all">全部日期</MenuItem>
                                            {streamDates.map(date => (
                                                <MenuItem key={date} value={date}>
                                                    {moment(date).format('YYYY-MM-DD')}
                                                    ({selectedUser.liveStreams.filter(s =>
                                                        s.startTime && moment(s.startTime).format('YYYY-MM-DD') === date
                                                    ).length}场)
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl variant="outlined" size="small" style={{ minWidth: 250, marginRight: 8 }}>
                                        <InputLabel id="stream-select-label">选择直播</InputLabel>
                                        <Select
                                            labelId="stream-select-label"
                                            value={selectedStreamIndex}
                                            onChange={handleStreamChange}
                                            label="选择直播"
                                            disabled={isEditing}
                                            style={{
                                                backgroundColor: '#252530',
                                                color: 'white',
                                                borderColor: '#444'
                                            }}
                                        >
                                            {/* 添加"全部"选项 */}
                                            <MenuItem value={ALL_STREAMS}>全部直播</MenuItem>
                                            {selectedUser.liveStreams.map((stream, index) => (
                                                <MenuItem key={index} value={index}>
                                                    {getLocalTimeString(stream.startTime, 'MM-DD HH:mm')} - {stream.title || `直播 #${index + 1}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* 编辑按钮区域 - 只在查看特定直播时显示 */}
                                    {selectedStreamIndex !== ALL_STREAMS && (
                                        <>
                                            {!isEditing ? (
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={<EditIcon />}
                                                    onClick={handleEditClick}
                                                    size="small"
                                                >
                                                    编辑数据
                                                </Button>
                                            ) : (
                                                <Box>
                                                    <Button
                                                        variant="outlined"
                                                        color="secondary"
                                                        size="small"
                                                        onClick={handleCancelEdit}
                                                        style={{ marginRight: 8 }}
                                                    >
                                                        取消
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        onClick={handleSaveStreamData}
                                                        disabled={isSaving}
                                                    >
                                                        {isSaving ? <CircularProgress size={20} /> : '保存'}
                                                    </Button>
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Box>

                            {/* 全部直播视图 */}
                            {selectedStreamIndex === ALL_STREAMS && (
                                <Card className={classes.streamCard}>
                                    <CardContent>
                                        <Typography variant="subtitle1" color="primary" gutterBottom>
                                            {selectedDate === 'all'
                                                ? '所有日期的直播数据'
                                                : `${moment(selectedDate).format('YYYY年MM月DD日')}的直播数据`}
                                        </Typography>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableHeaderCell>日期</TableHeaderCell>
                                                        <TableHeaderCell>直播标题</TableHeaderCell>
                                                        <TableHeaderCell>游戏</TableHeaderCell>
                                                        <TableHeaderCell>直播时长</TableHeaderCell>
                                                        <TableHeaderCell>观看时长</TableHeaderCell>
                                                        <TableHeaderCell>操作</TableHeaderCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {filteredStreams.map((stream, index) => {
                                                        const originalIndex = selectedUser.liveStreams.findIndex(s => s === stream);
                                                        return (
                                                            <TableRow key={stream.id || index}>
                                                                <TableCell>
                                                                    {getLocalTimeString(stream.startTime, 'MM-DD HH:mm')}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {stream.title || `直播 #${originalIndex + 1}`}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {stream.gameName || '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {formatMinutesToHoursAndMinutes(stream.streamTimeFormat || 0)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {formatMinutesToHoursAndMinutes(stream.cumulativeWatchTime || 0)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Button
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() => setSelectedStreamIndex(originalIndex)}
                                                                    >
                                                                        详情
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            )}

                            {/* 单个直播详情视图 */}
                            {selectedStreamIndex !== ALL_STREAMS && selectedUser.liveStreams.map((stream, index) => (
                                <Card
                                    key={index}
                                    className={classes.streamCard}
                                    style={{
                                        marginBottom: 16,
                                        display: index === selectedStreamIndex ? 'block' : 'none'
                                    }}
                                >
                                    {/* 直播标题 */}
                                    <Box className={classes.streamHeader}>
                                        <Typography variant="subtitle1" className={classes.streamTitle}>
                                            {stream.title || `直播 #${index + 1}`}
                                        </Typography>
                                    </Box>

                                    {/* 直播内容 */}
                                    <CardContent className={classes.streamContent}>
                                        {isEditing && index === selectedStreamIndex ? (
                                            /* 编辑表单 */
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom color="primary">
                                                    编辑直播数据
                                                </Typography>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            label="打赏奖励"
                                                            name="totalRewardPoints"
                                                            type="number"
                                                            fullWidth
                                                            variant="outlined"
                                                            value={editFormData.totalRewardPoints}
                                                            onChange={handleFormChange}
                                                            InputProps={{
                                                                endAdornment: <InputAdornment position="end">点</InputAdornment>,
                                                                style: { color: '#ff9a2e' }
                                                            }}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            label="直播时长"
                                                            name="streamTimeFormat"
                                                            type="number"
                                                            fullWidth
                                                            variant="outlined"
                                                            value={editFormData.streamTimeFormat}
                                                            onChange={handleFormChange}
                                                            InputProps={{
                                                                endAdornment: <InputAdornment position="end">分钟</InputAdornment>,
                                                            }}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            label="累计观看时长"
                                                            name="cumulativeWatchTime"
                                                            type="number"
                                                            fullWidth
                                                            variant="outlined"
                                                            value={editFormData.cumulativeWatchTime}
                                                            onChange={handleFormChange}
                                                            InputProps={{
                                                                endAdornment: <InputAdornment position="end">分钟</InputAdornment>,
                                                            }}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Box mt={2}>
                                                    <Typography variant="caption" color="textSecondary">
                                                        注意：修改这些数据会影响用户在排行榜中的位置和分数。保存后系统将重新计算排名。
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            /* 直播数据展示 */
                                            <>
                                                {/* 直播基本信息卡片 */}
                                                <Paper
                                                    elevation={0}
                                                    style={{
                                                        backgroundColor: 'rgba(33, 150, 243, 0.05)',
                                                        padding: 16,
                                                        borderRadius: 8,
                                                        marginBottom: 16
                                                    }}
                                                >
                                                    <Typography variant="subtitle1" style={{ color: '#2196f3', fontWeight: 500, marginBottom: 12 }}>
                                                        <InfoOutlinedIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                                        基本信息
                                                    </Typography>

                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12} md={6}>
                                                            <Box display="flex" alignItems="center" mb={1.5}>
                                                                <Typography style={{ color: '#9b9b9b', width: 100 }}>直播标题:</Typography>
                                                                <Typography style={{ fontWeight: 500 }}>{stream.title || '未命名直播'}</Typography>
                                                            </Box>
                                                            <Box display="flex" alignItems="center" mb={1.5}>
                                                                <Typography style={{ color: '#9b9b9b', width: 100 }}>游戏:</Typography>
                                                                <Typography style={{ fontWeight: 500 }}>{stream.gameName || '未知游戏'}</Typography>
                                                            </Box>
                                                            <Box display="flex" alignItems="center">
                                                                <Typography style={{ color: '#9b9b9b', width: 100 }}>数据来源:</Typography>
                                                                <Typography style={{ fontWeight: 500 }}>{stream.dataSource || '-'}</Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <Box display="flex" alignItems="center" mb={1.5}>
                                                                <CalendarTodayIcon style={{ color: '#9b9b9b', marginRight: 8, fontSize: 18 }} />
                                                                <Typography style={{ color: '#9b9b9b', marginRight: 8 }}>开始时间:</Typography>
                                                                <Typography style={{ fontWeight: 500 }}>{getLocalTimeString(stream.startTime, 'YYYY-MM-DD HH:mm')}</Typography>
                                                            </Box>
                                                            <Box display="flex" alignItems="center">
                                                                <CalendarTodayIcon style={{ color: '#9b9b9b', marginRight: 8, fontSize: 18 }} />
                                                                <Typography style={{ color: '#9b9b9b', marginRight: 8 }}>结束时间:</Typography>
                                                                <Typography style={{ fontWeight: 500 }}>{getLocalTimeString(stream.endTime, 'YYYY-MM-DD HH:mm')}</Typography>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Paper>

                                                {/* 关键指标卡片 */}
                                                <Box className={classes.streamStatsContainer} mb={2}>
                                                    <Paper className={classes.streamStatItem} style={{ backgroundColor: 'rgba(33, 150, 243, 0.08)' }}>
                                                        <div className={classes.streamStatValue} style={{ color: '#2196f3' }}>{stream.averageViewers || 0}</div>
                                                        <div className={classes.streamStatLabel}>平均观众</div>
                                                    </Paper>
                                                    <Paper className={classes.streamStatItem} style={{ backgroundColor: 'rgba(156, 39, 176, 0.08)' }}>
                                                        <div className={classes.streamStatValue} style={{ color: '#9c27b0' }}>{stream.peakViewers || 0}</div>
                                                        <div className={classes.streamStatLabel}>最高观众</div>
                                                    </Paper>
                                                    <Paper className={classes.streamStatItem} style={{ backgroundColor: 'rgba(0, 150, 136, 0.08)' }}>
                                                        <div className={classes.streamStatValue} style={{ color: '#009688' }}>
                                                            {formatMinutesToHoursAndMinutes(stream.streamTimeFormat || 0, false)}
                                                        </div>
                                                        <div className={classes.streamStatLabel}>直播时长</div>
                                                    </Paper>
                                                    <Paper className={classes.streamStatItem} style={{ backgroundColor: 'rgba(255, 152, 0, 0.08)' }}>
                                                        <div className={classes.streamStatValue} style={{ color: '#ff9800' }}>
                                                            {formatMinutesToHoursAndMinutes(stream.cumulativeWatchTime || 0, false)}
                                                        </div>
                                                        <div className={classes.streamStatLabel}>累计观看</div>
                                                    </Paper>
                                                </Box>

                                                {/* 奖励数据卡片 */}
                                                <Paper
                                                    elevation={0}
                                                    style={{
                                                        backgroundColor: 'rgba(76, 175, 80, 0.05)',
                                                        padding: 16,
                                                        borderRadius: 8,
                                                        marginBottom: 16
                                                    }}
                                                >
                                                    <Typography variant="subtitle1" style={{ color: '#4caf50', fontWeight: 500, marginBottom: 12 }}>
                                                        <MonetizationOnIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                                        奖励数据
                                                    </Typography>

                                                    <Grid container spacing={4}>
                                                        <Grid item xs={12} md={6}>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                p={2}
                                                                bgcolor="rgba(145, 70, 255, 0.1)"
                                                                borderRadius={1}
                                                            >
                                                                <Box display="flex" alignItems="center">
                                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="#9146FF" style={{ marginRight: 12 }}>
                                                                        <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
                                                                    </svg>
                                                                    <Typography style={{ fontWeight: 500 }}>订阅奖励</Typography>
                                                                </Box>
                                                                <Typography variant="h6" style={{ fontWeight: 'bold', color: '#9146FF' }}>
                                                                    {stream.tierRewardPoints || 0} <small style={{ fontSize: '0.8rem' }}>点</small>
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                p={2}
                                                                bgcolor="rgba(255, 154, 46, 0.1)"
                                                                borderRadius={1}
                                                            >
                                                                <Box display="flex" alignItems="center">
                                                                    <MonetizationOnIcon style={{ color: '#ff9a2e', marginRight: 12 }} />
                                                                    <Typography style={{ fontWeight: 500 }}>打赏奖励</Typography>
                                                                </Box>
                                                                <Typography variant="h6" style={{ fontWeight: 'bold', color: '#ff9a2e' }}>
                                                                    {stream.bitsRewardPoints || 0} <small style={{ fontSize: '0.8rem' }}>点</small>
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>

                                                    {/* 添加合计奖励显示 */}
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        p={2}
                                                        bgcolor="rgba(33, 150, 243, 0.1)"
                                                        borderRadius={1}
                                                        mt={2}
                                                    >
                                                        <Box display="flex" alignItems="center">
                                                            <MonetizationOnIcon style={{ color: '#2196f3', marginRight: 12 }} />
                                                            <Typography style={{ fontWeight: 500 }}>合计奖励</Typography>
                                                        </Box>
                                                        <Typography variant="h6" style={{ fontWeight: 'bold', color: '#2196f3' }}>
                                                            {((stream.totalRewardPoints || 0)).toLocaleString()}
                                                            <small style={{ fontSize: '0.8rem' }}>点</small>
                                                        </Typography>
                                                    </Box>
                                                </Paper>

                                                {/* 时间详情卡片 */}
                                                <Paper
                                                    elevation={0}
                                                    style={{
                                                        backgroundColor: 'rgba(0, 150, 136, 0.05)',
                                                        padding: 16,
                                                        borderRadius: 8
                                                    }}
                                                >
                                                    <Typography variant="subtitle1" style={{ color: '#009688', fontWeight: 500, marginBottom: 12 }}>
                                                        <AccessTimeIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                                        时长详情
                                                    </Typography>

                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12} md={6}>
                                                            <Box className={classes.streamDetailItem} style={{ borderColor: 'rgba(0, 150, 136, 0.2)' }}>
                                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                    <Typography style={{ color: '#9b9b9b' }}>直播时长:</Typography>
                                                                    <Typography style={{ fontWeight: 'bold' }}>
                                                                        {formatMinutesToHoursAndMinutes(stream.streamTimeFormat || 0)}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <Box className={classes.streamDetailItem} style={{ borderColor: 'rgba(0, 150, 136, 0.2)' }}>
                                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                    <Typography style={{ color: '#9b9b9b' }}>累计观看时长:</Typography>
                                                                    <Typography style={{ fontWeight: 'bold' }}>
                                                                        {formatMinutesToHoursAndMinutes(stream.cumulativeWatchTime || 0)}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Paper>
                                            </>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </>
                    ) : (
                        <Box className={classes.noDataMessage}>
                            <InfoIcon style={{ fontSize: 48, color: '#555', marginBottom: 16 }} />
                            <Typography variant="h6">暂无直播数据</Typography>
                            <Typography variant="body2" style={{ maxWidth: 400, margin: '8px auto 0' }}>
                                该用户尚未记录任何直播数据，或数据可能尚未同步。
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        );
    };

    // 渲染排行榜表格
    const renderLeaderboardTable = () => {
        if (leaderboard.length === 0) {
            return (
                <Box className={classes.noData}>
                    <Typography variant="body1">暂无排行榜数据</Typography>
                </Box>
            );
        }

        return (
            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableHeaderCell width="120px">
                                <Tooltip title="用户排名" placement="top" arrow>
                                    <Box display="flex" alignItems="center">
                                        <TrophyIcon style={{ marginRight: 8 }} />
                                        排名
                                    </Box>
                                </Tooltip>
                            </TableHeaderCell>
                            <TableHeaderCell width="120px">用户名</TableHeaderCell>
                            <TableHeaderCell width="140px">Twitch 用户名</TableHeaderCell>
                            <TableHeaderCell width="120px">
                                <Tooltip title="排行总分" placement="top" arrow>
                                    <Box display="flex" alignItems="center">
                                        <SortIcon style={{ marginRight: 8 }} />
                                        总分
                                    </Box>
                                </Tooltip>
                            </TableHeaderCell>
                            <TableHeaderCell width="120px">
                                <Tooltip title="直播总时长" placement="top" arrow>
                                    <Box display="flex" alignItems="center">
                                        <TimerIcon style={{ marginRight: 8 }} />
                                        直播时长
                                    </Box>
                                </Tooltip>
                            </TableHeaderCell>
                            <TableHeaderCell width="120px">
                                <Tooltip title="直播天数" placement="top" arrow>
                                    <Box display="flex" alignItems="center">
                                        <CalendarTodayIcon style={{ marginRight: 8 }} />
                                        直播天数
                                    </Box>
                                </Tooltip>
                            </TableHeaderCell>
                            <TableHeaderCell width="140px">
                                <Tooltip title="累计观看时长" placement="top" arrow>
                                    <Box display="flex" alignItems="center">
                                        <VisibilityIcon style={{ marginRight: 8 }} />
                                        观看时长
                                    </Box>
                                </Tooltip>
                            </TableHeaderCell>
                            <TableHeaderCell width="140px">
                                <Tooltip title="打赏奖励" placement="top" arrow>
                                    <Box display="flex" alignItems="center">
                                        <MonetizationOnIcon style={{ marginRight: 8 }} />
                                        打赏奖励
                                    </Box>
                                </Tooltip>
                            </TableHeaderCell>
                            <TableHeaderCell width="140px">
                                <Tooltip title="最终奖励金额" placement="top" arrow>
                                    <Box display="flex" alignItems="center">
                                        <MonetizationOnIcon style={{ marginRight: 8, color: '#4caf50' }} />
                                        奖励金额
                                    </Box>
                                </Tooltip>
                            </TableHeaderCell>
                            <TableHeaderCell width="80px" align="center" className={classes.actionColumnHeader}>
                                详情
                            </TableHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {leaderboard.map((row, index) => {
                            const userQuest = row.userQuest || {};

                            return (
                                <TableRow key={index} className={`${classes.tableRow} ${row.rank <= 3 ? `top-rank rank-${row.rank}` : ''}`}>
                                    <TableCell className={classes.tableCell}>
                                        {row.rank <= 3 ? (
                                            <Box display="flex" alignItems="center">
                                                <span className={`rank-${row.rank}`} style={{ fontSize: '1.2rem', marginRight: '4px' }}>
                                                    {row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : '🥉'}
                                                </span>
                                                {row.rank}
                                            </Box>
                                        ) : row.rank}
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        {row.user?.displayName || row.user?.username || '未知用户'}
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <Box display="flex" alignItems="center">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="#9146FF" style={{ minWidth: 16, marginRight: 8 }}>
                                                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
                                            </svg>
                                            {row.twitchName || userQuest.twitchUsername || row.user?.connectedAccounts?.twitch?.username || '-'}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <Typography variant="body2" style={{ fontWeight: 500, color: '#2196f3' }}>
                                            {Math.round(row.overallScore || 0).toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <Typography variant="body2" style={{ color: '#ff9800' }}>
                                            {formatMinutesToHoursAndMinutes(userQuest.liveStreamingTime || 0)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <Chip
                                            label={formatValueWithUnit(userQuest.liveStreamingDays || 0, 'day')}
                                            size="small"
                                            style={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50' }}
                                        />
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <Typography variant="body2" style={{ color: '#9c27b0' }}>
                                            {formatMinutesToHoursAndMinutes(userQuest.viewingTime || 0)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                                            {userQuest.rewardData ? `${userQuest.rewardData.toFixed(2)}` : '0.00'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <Typography variant="body2" style={{ fontWeight: 700, color: '#4caf50' }}>
                                            ${row.rewardAmount ? row.rewardAmount.toFixed(2) : '0.00'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className={`${classes.tableCell} ${classes.actionColumn}`} align="center">
                                        <Button
                                            variant="contained"
                                            size="small"
                                            className={classes.detailButton}
                                            onClick={() => handleOpenDetail(row)}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    // 渲染任务信息卡片
    const renderQuestInfo = () => {
        if (!questInfo) return null;

        const { statusText, statusClass } = getQuestStatusInfo(questInfo);

        // 默认权重值，这些值应该与后端计算保持一致
        const weights = {
            Wt: 0.8,  // 观看时间权重 
            Wr: 0.1,    // 奖励数据权重
            Wd: 0.1   // 直播天数权重
        };

        return (
            <Card className={classes.questDetails} elevation={3}>
                <CardContent>
                    <Typography variant="h6" className={classes.questTitle}>
                        <InfoIcon style={{ color: '#2196f3' }} />
                        {questInfo.title}
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>任务状态:</div>
                                <div className={classes.questDetailValue}>
                                    <Chip
                                        label={statusText}
                                        className={`${classes.statusChip} ${statusClass}`}
                                        size="small"
                                    />
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>任务类型:</div>
                                <div className={classes.questDetailValue}>
                                    {questInfo.type || '未知类型'}
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>游戏名称:</div>
                                <div className={classes.questDetailValue}>
                                    {questInfo.game?.name || '未知游戏'}
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>
                                    <AccessTimeIcon />
                                    开始时间:
                                </div>
                                <div className={classes.questDetailValue}>
                                    {getLocalTimeString(questInfo.startDateTime, 'YYYY-MM-DD HH:mm')}
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>
                                    <AccessTimeIcon />
                                    结束时间:
                                </div>
                                <div className={classes.questDetailValue}>
                                    {getLocalTimeString(questInfo.endDateTime, 'YYYY-MM-DD HH:mm')}
                                </div>
                            </div>
                        </Grid>
                    </Grid>

                    <Divider style={{ margin: '20px 0' }} />

                    {/* 权重计算公式区域 */}
                    <Typography variant="subtitle1" style={{ color: '#9b9b9b', marginBottom: '10px' }}>
                        <Tooltip title="排行榜分数计算权重占比" placement="top" arrow>
                            <Box display="flex" alignItems="center">
                                <InfoOutlinedIcon style={{ marginRight: 8, fontSize: '1rem' }} />
                                计算权重
                            </Box>
                        </Tooltip>
                    </Typography>

                    <div className={classes.statsContainer} style={{ marginBottom: '20px' }}>
                        <div className={classes.statCard}>
                            <VisibilityIcon color="primary" />
                            <div className={classes.statValue}>{(weights.Wt * 100).toFixed(0)}%</div>
                            <div className={classes.statLabel}>观看时间</div>
                        </div>
                        <div className={classes.statCard}>
                            <CalendarTodayIcon color="primary" />
                            <div className={classes.statValue}>{(weights.Wd * 100).toFixed(0)}%</div>
                            <div className={classes.statLabel}>直播天数</div>
                        </div>
                        <div className={classes.statCard}>
                            <MonetizationOnIcon color="primary" />
                            <div className={classes.statValue}>{(weights.Wr * 100).toFixed(0)}%</div>
                            <div className={classes.statLabel}>打赏奖励</div>
                        </div>
                    </div>

                    <Divider style={{ margin: '20px 0' }} />

                    {/* 原有的统计数据区域 */}
                    <div className={classes.statsContainer}>
                        <div className={classes.statCard}>
                            <GroupIcon color="primary" />
                            <div className={classes.statValue}>{participantCount}</div>
                            <div className={classes.statLabel}>参与人数</div>
                        </div>
                        <div className={classes.statCard}>
                            <TimerIcon color="primary" />
                            <div className={classes.statValue}>{totalStreamingHours}</div>
                            <div className={classes.statLabel}>总直播小时</div>
                        </div>
                        <div className={classes.statCard}>
                            <VisibilityIcon color="primary" />
                            <div className={classes.statValue}>
                                {formatMinutesToHoursAndMinutes(totalWatchTime, false) || '0h'}
                            </div>
                            <div className={classes.statLabel}>总观看时间</div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    // 主渲染
    return (
        <Container maxWidth="xl">
            <Box className={classes.container}>
                <Typography variant="h4" gutterBottom>任务排行榜管理</Typography>
                <Typography className={classes.description}>
                    <InfoOutlinedIcon />
                    查询特定任务的实时排行榜数据，或执行批量任务操作
                </Typography>
                {/* 搜索表单 */}
                <Box className={classes.searchForm}>
                    <TextField
                        className={classes.searchField}
                        label="请输入任务ID"
                        variant="outlined"
                        value={questId}
                        onChange={handleQuestIdChange}
                        onKeyPress={(e) => handleKeyPress(e)}
                        placeholder="例如: 640de1a337f453641e9e164e"
                    />
                    <Button
                        className={classes.searchButton}
                        variant="contained"
                        onClick={fetchLeaderboard}
                        disabled={isLoading || !questId}
                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    >
                        {isLoading ? '查询中...' : '查询排行榜'}
                    </Button>

                    {/* 添加同步按钮，仅在有有效查询结果且为 tiered-auto 类型时显示 */}
                    {questInfo && isAutoStreamQuest(questInfo) && (
                        <Button
                            className={classes.syncButton}
                            variant="contained"
                            onClick={handleOpenSyncDialog}
                            disabled={isLoading || isSyncing}
                            startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                        >
                            {isSyncing ? '同步中...' : '同步数据'}
                        </Button>
                    )}

                    {/* 添加重置按钮，只要有查询结果就显示 */}
                    {questInfo && (
                        <Button
                            className={classes.resetButton}
                            variant="contained"
                            onClick={handleOpenResetDialog}
                            disabled={isLoading || isResetting}
                            startIcon={isResetting ? <CircularProgress size={20} color="inherit" /> : <SettingsBackupRestoreIcon />}
                        >
                            {isResetting ? '重置中...' : '重置数据'}
                        </Button>
                    )}
                </Box>

                {/* 添加操作提示框 */}
                {actionAlert.show && (
                    <Box className={classes.alertContainer}>
                        <Alert severity={actionAlert.severity} variant="filled" onClose={() => setActionAlert(prev => ({ ...prev, show: false }))}>
                            {actionAlert.message}
                        </Alert>
                    </Box>
                )}

                {/* 添加三个操作按钮 */}
                <Box className={classes.actionButtonsContainer}>
                    <Button
                        className={`${classes.actionButton} ${classes.syncButtonAll}`}
                        onClick={handleSyncAllQuestsYesterday}
                        disabled={isProcessing.syncYesterday}
                        startIcon={isProcessing.syncYesterday ? <CircularProgress size={24} color="inherit" /> : <SyncIcon className={classes.buttonIcon} />}
                    >
                        <Box className={classes.buttonContent}>
                            <Typography variant="subtitle1" className={classes.buttonTitle}>
                                同步所有任务昨日排行榜
                            </Typography>
                            <Typography variant="body2" className={classes.buttonDescription}>
                                更新所有任务的昨日排行榜数据
                            </Typography>
                        </Box>
                    </Button>

                    <Button
                        className={`${classes.actionButton} ${classes.endButton}`}
                        onClick={handleEndAllQuests}
                        disabled={isProcessing.endQuests}
                        startIcon={isProcessing.endQuests ? <CircularProgress size={24} color="inherit" /> : <DoneAllIcon className={classes.buttonIcon} />}
                    >
                        <Box className={classes.buttonContent}>
                            <Typography variant="subtitle1" className={classes.buttonTitle}>
                                结束当前时段所有任务
                            </Typography>
                            <Typography variant="body2" className={classes.buttonDescription}>
                                为24小时内应结束的任务执行结束操作
                            </Typography>
                        </Box>
                    </Button>

                    <Button
                        className={`${classes.actionButton} ${classes.checkoutButton}`}
                        onClick={handleCheckoutAllQuests}
                        disabled={isProcessing.checkoutQuests}
                        startIcon={isProcessing.checkoutQuests ? <CircularProgress size={24} color="inherit" /> : <AccountBalanceWalletIcon className={classes.buttonIcon} />}
                    >
                        <Box className={classes.buttonContent}>
                            <Typography variant="subtitle1" className={classes.buttonTitle}>
                                自动结算所有任务
                            </Typography>
                            <Typography variant="body2" className={classes.buttonDescription}>
                                对24小时内的已结束任务执行结算操作
                            </Typography>
                        </Box>
                    </Button>
                </Box>

                <Dialog
                    open={openSyncDialog}
                    onClose={handleCloseSyncDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        style: {
                            backgroundColor: '#1e1e28',
                            borderRadius: 8,
                        },
                    }}
                >
                    <DialogTitle className={classes.dialogTitle}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">
                                同步任务排行榜数据
                            </Typography>
                            <IconButton
                                className={classes.closeButton}
                                onClick={handleCloseSyncDialog}
                                size="small"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent className={classes.dialogContent}>
                        <Box mb={2}>
                            <Typography variant="body1" gutterBottom>
                                您将同步 <strong style={{ color: '#2196f3' }}>{questInfo?.title || ''}</strong> 的排行榜数据
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                请选择要同步的时间范围和选项
                            </Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    className={classes.datePicker}
                                    margin="normal"
                                    id="sync-start-date"
                                    label="开始时间"
                                    type="date"
                                    fullWidth
                                    variant="outlined"
                                    value={syncStartDate ? syncStartDate.toISOString().split('T')[0] : ''}
                                    onChange={(e) => {
                                        const newDate = new Date(e.target.value);
                                        setSyncStartDate(newDate);
                                        // 更新resetData设置
                                        updateResetDataBasedOnDateRange(newDate, syncEndDate);
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                        style: { color: '#aaa' }
                                    }}
                                    inputProps={{
                                        min: questInfo?.startDateTime ? new Date(questInfo.startDateTime).toISOString().split('T')[0] : '',
                                        max: syncEndDate ? syncEndDate.toISOString().split('T')[0] : '',
                                    }}
                                    disabled={isSyncing}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    className={classes.datePicker}
                                    margin="normal"
                                    id="sync-end-date"
                                    label="结束时间"
                                    type="date"
                                    fullWidth
                                    variant="outlined"
                                    value={syncEndDate ? syncEndDate.toISOString().split('T')[0] : ''}
                                    onChange={(e) => {
                                        const newDate = new Date(e.target.value);
                                        setSyncEndDate(newDate);
                                        // 更新resetData设置
                                        updateResetDataBasedOnDateRange(syncStartDate, newDate);
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                        style: { color: '#aaa' }
                                    }}
                                    inputProps={{
                                        min: syncStartDate ? syncStartDate.toISOString().split('T')[0] : '',
                                        max: questInfo?.endDateTime ? new Date(questInfo.endDateTime).toISOString().split('T')[0] : '',
                                    }}
                                    disabled={isSyncing}
                                />
                            </Grid>
                        </Grid>

                        {/* <Box mt={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={resetData}
                                    onChange={(e) => setResetData(e.target.checked)}
                                    name="resetData"
                                    color="primary"
                                    disabled={isSyncing}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2">重置数据并重新计算</Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        选中此项将清除指定时间范围内的数据并重新计算，否则仅更新数据
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box> */}

                        <Box mt={2} p={2} bgcolor={resetData ? "rgba(33, 150, 243, 0.1)" : "rgba(255, 152, 0, 0.1)"} borderRadius={1}>
                            <Typography variant="body2" color="textSecondary">
                                <InfoOutlinedIcon style={{ fontSize: 16, verticalAlign: 'text-bottom', marginRight: 4 }} />
                                {resetData ? (
                                    <span>系统将<strong style={{ color: '#2196f3' }}>重置所选时间范围内的全部数据</strong>并重新计算，因为您选择了任务的完整时间范围。</span>
                                ) : (
                                    <span>系统将<strong style={{ color: '#ff9800' }}>仅增量更新所选时间范围内的数据</strong>，因为您选择了任务的部分时间范围。</span>
                                )}
                            </Typography>
                        </Box>

                        {syncError && (
                            <Box mt={2} bgcolor="rgba(255, 90, 95, 0.1)" p={2} borderRadius={4}>
                                <Typography variant="body2" style={{ color: '#ff5a5f' }}>
                                    <ErrorIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                    {syncError}
                                </Typography>
                            </Box>
                        )}

                        {syncSuccess && (
                            <Box mt={2} bgcolor="rgba(76, 175, 80, 0.1)" p={2} borderRadius={4}>
                                <Typography variant="body2" style={{ color: '#4caf50' }}>
                                    <CheckCircleIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                    {syncSuccess}
                                </Typography>

                                {syncStats && (
                                    <Box mt={1}>
                                        <Typography variant="caption" style={{ color: '#4caf50', fontWeight: 500 }}>
                                            同步统计:
                                        </Typography>
                                        <Box pl={2} mt={0.5}>
                                            <Typography variant="caption" display="block">
                                                处理用户: {syncStats.processedUsersCount || 0}
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                处理直播: {syncStats.processedStreamsCount || 0}
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                                处理时间: {(syncStats.processingTimeMs / 1000).toFixed(2)}秒
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions style={{ backgroundColor: '#1e1e28', padding: '16px 24px' }}>
                        <Button
                            onClick={handleCloseSyncDialog}
                            color="default"
                            className={classes.dialogButton}
                            disabled={isSyncing}
                        >
                            取消
                        </Button>
                        <Button
                            onClick={handleSyncData}
                            color="primary"
                            variant="contained"
                            className={classes.dialogButton}
                            disabled={isSyncing || !syncStartDate || !syncEndDate}
                            startIcon={isSyncing ? <CircularProgress size={20} /> : <SyncIcon />}
                        >
                            {isSyncing ? '同步中...' : '开始同步'}
                        </Button>
                    </DialogActions>

                </Dialog>

                {/* 错误提示 */}
                {error && (
                    <Box className={classes.errorMessage}>
                        <ErrorIcon /> {error}
                    </Box>
                )}

                {/* 任务信息 */}
                {questInfo && renderQuestInfo()}

                {/* 排行榜表格 */}
                {hasSearched && leaderboard.length > 0 && renderLeaderboardTable()}

                {/* 直播详情对话框 */}
                {openDetailDialog && renderStreamDetailDialog()}

                {/* 新增直播对话框 */}
                {isAddingStream && renderAddStreamDialog()}

                {/* 重置数据对话框 */}
                {renderResetDialog()}

                {/* 添加 PromptDialog 组件 */}
                {dialogOpen && (
                    <PromptDialog
                        dialogOpen={dialogOpen}
                        level={dialogLevel}
                        title={dialogTitle}
                        content={dialogContent}
                        cancelAction={handleDialogClose}
                        cancelButtonName="确定"
                    />
                )}
            </Box>
        </Container>
    );
}

export default QuestLeaderboard;